/* eslint-disable enforce-catch-error-handled/require-catch-error-handling */
// TODO: Remove this eslint-disable comment in a separate PR (RCP-27576)
import {
  CreateIssueRequestDto,
  CreateIssueRequestDtoSeverityEnum,
  CreateIssueResponseDto,
  IssueSourceDto,
  SaveActionRequestDto,
} from '@issuemanager/api-client';
import {
  CollapsibleField,
  ControlledDateField,
  ControlledFileUploader,
  ControlledTextField,
  ExtendedControlledAutocomplete,
  ExtendedTypography,
  GovmapGroupsSelector,
  ReadOnlyText,
  UserSelector,
} from '@issuemanager/apps/client/components';
import { useAttachmentsContext } from '@issuemanager/apps/client/contexts';
import {
  useAllMutations,
  useAllQueries,
  useBadRequestOrConflictError,
  useMonitorCreateIssueFormIssueSubtypesLoad,
  useMonitorCreateIssueFormSubmit,
} from '@issuemanager/apps/client/hooks';
import {
  DEBOUNCE_TIME,
  formHasErrorsToDisplay,
  getFormStateErrors,
  joinFormErrorsWithServiceErrors,
  mapFormLabelsToFieldLabel,
} from '@issuemanager/apps/client/utils';
import { useInfiniteIssueSubtypes } from '@issuemanager/client/features/issue-subtypes';
import { useIssueTemplateByIssueTypeAndIssueSubtype } from '@issuemanager/client/features/issue-templates';
import {
  useCreateIssue,
  useLocalizedClassification,
  useLocalizedIssueTypes,
  useSeverityLocalizer,
} from '@issuemanager/client/features/issues';
import {
  useSLAHelperText,
  useSLAsByIssueTypeAndIssueSubtype,
} from '@issuemanager/client/features/slas';
import { useFeatureFlags } from '@issuemanager/client/features/users';
import {
  ISSUE_DESCRIPTION_LENGTH_MAX,
  ISSUE_IMPACT_LENGTH_MAX,
  ISSUE_SUBTYPE_NONE_VALUE,
  ISSUE_TITLE_LENGTH_MAX,
  IssueClassificationEnum,
  IssueType,
  IssueTypeEnum,
  RECOMMENDATIONS_LENGTH_MAX,
  ROOT_CAUSE_LENGTH_MAX,
} from '@issuemanager/constants';
import {
  AutocompleteInputChangeReason,
  Checkbox,
  FormControlLabel,
  Link,
  Stack,
  Typography,
  debounce,
  useEventCallback,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  getSLAMaxDateTime,
  isDateEarlierThanSLA,
} from '../utils/sla-validation';
import { useTemplateApplication } from './use-template-application';

type LocalFields =
  | 'title'
  | 'issueType'
  | 'issueSubtype'
  | 'description'
  | 'impact'
  | 'attachments'
  | 'information'
  | 'source'
  | 'govmapGroups'
  | 'dueDate'
  | 'ownerId'
  | 'severity'
  | 'classification'
  | 'rootCause'
  | 'recommendations'
  | 'includeInAuditReport';

export type UseCreateIssueFormProps = {
  information?: string;
  onCreated?: (values: CreateIssueResponseDto) => void;
  onError?: (error: unknown) => void;
  answerIds?: string[];
  defaultValues?: Partial<Omit<CreateIssueRequestDto, 'answerIds'>>;
  sourceInfo?: IssueSourceDto;
  issueType?: IssueType;
  open?: boolean;
  isSourceError?: boolean;
  isSourceExpected?: boolean;
  readOnly?: boolean;
  existingActions?: SaveActionRequestDto[];
};

type UseCreateIssueFormReturnType = {
  form: ReturnType<typeof useForm<CreateIssueRequestDto>>;
  issueTypeSubtypeQueryIsLoading: boolean;
  isTemplateLoading: boolean;
  createIssueIsLoading: boolean;
  createIssueIsSuccess: boolean;
  fields: Record<LocalFields, ReactNode>;
  errors: ReturnType<typeof joinFormErrorsWithServiceErrors>;
  hasErrors: boolean;
  handleCreate: () => void;
  handleSave: (saveIssueMutation: {
    mutateAsync: (
      values: CreateIssueRequestDto,
    ) => Promise<CreateIssueResponseDto>;
  }) => void;
  cleanErrors: () => void;
  cleanForm: () => void;
  shouldShowExtendedFields: boolean;
  showControlDeficiencyExtraFields?: boolean;
  showAuditFindingExtraFields?: boolean;
};

const normalizeSlaIssueSubtype = (
  selectedIssueSubtype: string | undefined,
): string | undefined => {
  if (!selectedIssueSubtype) return undefined;
  if (selectedIssueSubtype === ISSUE_SUBTYPE_NONE_VALUE) return 'null';
  return selectedIssueSubtype;
};

export const useCreateIssueForm = (
  props: UseCreateIssueFormProps,
): UseCreateIssueFormReturnType => {
  const {
    open = true,
    onCreated,
    answerIds,
    information,
    readOnly = false,
    isSourceError,
    isSourceExpected,
    onError,
    sourceInfo,
    issueType,
    existingActions,
  } = props;
  const { enqueueSnackbar } = useSnackbar();
  const { isLoading: isAttachmentLoading } = useAttachmentsContext();

  const { t } = useTranslation(['issue', 'common']);
  const { errors: serviceErrors, cleanErrors: cleanServiceErrors } =
    useBadRequestOrConflictError();

  const isEditingExistingIssue = !!props.defaultValues?.issueType;

  const isDraftIssuesEnabled = useFeatureFlags('draftIssues');
  const isIssueTypesExtraFieldsEnabled = useFeatureFlags(
    'issueTypesExtraFields',
  );

  const createIssue = useCreateIssue({
    onSuccess: (values) => {
      enqueueSnackbar('', {
        variant: 'success',
        description: (
          <Trans
            t={t}
            i18nKey='form.tooltip.success'
            components={{ b: <b /> }}
            values={{ issueCode: values.code }}
          />
        ),
      });
    },
  });

  const defaultValues = useMemo(
    () => ({
      description: '',
      impact: '',
      attachments: [],
      issueType: undefined,
      issueSubtypeId: '',
      rootCause: '',
      recommendations: '',
      includeInAuditReport: true,
      ...props.defaultValues,
    }),
    [props.defaultValues],
  );

  const form = useForm<CreateIssueRequestDto>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues,
  });

  const selectedIssueType = form.watch('issueType');
  const selectedIssueSubtype = form.watch('issueSubtypeId');
  const selectedSeverity = form.watch('severity');

  const issueTypeAndIssueSubtypeSelected =
    Boolean(selectedIssueType) &&
    selectedIssueSubtype !== undefined &&
    selectedIssueSubtype !== '';

  const {
    data: templateData,
    isFetching: isTemplateFetching,
    dataUpdatedAt,
  } = useIssueTemplateByIssueTypeAndIssueSubtype({
    issueType: selectedIssueType,
    issueSubtypeId:
      selectedIssueSubtype === ISSUE_SUBTYPE_NONE_VALUE
        ? undefined
        : selectedIssueSubtype,
    enabled: issueTypeAndIssueSubtypeSelected && !isEditingExistingIssue,
  });

  const { resetTemplateState } = useTemplateApplication({
    form,
    templateData: isEditingExistingIssue ? undefined : templateData,
    dataUpdatedAt,
    issueTypeAndIssueSubtypeSelected:
      issueTypeAndIssueSubtypeSelected && !isEditingExistingIssue,
  });

  const [subtypeFilterByText, setSubtypeFilterByText] = useState<string>('');

  const issueSubtypesQuery = useInfiniteIssueSubtypes({
    params: {
      filterByName: subtypeFilterByText,
      filterByEnabled: true,
      filterByIssueTypes: selectedIssueType ? [selectedIssueType] : [],
    },
    enabled: !!selectedIssueType,
    handleError: true,
  });

  const { data: SLAs } = useSLAsByIssueTypeAndIssueSubtype({
    issueType: selectedIssueType,
    issueSubtype: normalizeSlaIssueSubtype(selectedIssueSubtype),
  });

  const slaHelperText = useSLAHelperText({
    SLAs,
    severity: selectedSeverity,
  });

  const slaMaxDateTime = useMemo(
    () => getSLAMaxDateTime(SLAs, selectedSeverity),
    [SLAs, selectedSeverity],
  );

  const subtypesOptions =
    issueSubtypesQuery.data?.pages.map((c) => c.data).flat() ?? [];

  const handleInputChange = useCallback(
    debounce(
      (
        value: string,
        _field: 'issueType' | 'issueSubtype',
        reason: AutocompleteInputChangeReason,
      ) => {
        if (reason === 'reset') return;
        setSubtypeFilterByText(value);
      },
      DEBOUNCE_TIME,
    ),
    [],
  );

  const issueTypeSubtypeQueryIsLoading = issueSubtypesQuery.isFetching;

  const isControlDeficiencySelected =
    selectedIssueType === IssueTypeEnum.CONTROL_DEFICIENCY;

  const isAuditFindingSelected =
    selectedIssueType === IssueTypeEnum.AUDIT_FINDING;

  const localizedIssueTypes = useLocalizedIssueTypes();
  const { getSeverityLabel } = useSeverityLocalizer();
  const severityValues = Object.entries(CreateIssueRequestDtoSeverityEnum).map(
    ([_key, value]) => ({
      label: getSeverityLabel(value) ?? value,
      value,
    }),
  );

  const { getClassificationLabel } = useLocalizedClassification();
  const classificationValues = Object.values(IssueClassificationEnum).map(
    (value) => ({
      label: getClassificationLabel(value) ?? value,
      value,
    }),
  );

  const issueTypeEnumOptions = useMemo(
    () =>
      Object.values(IssueTypeEnum).map((enumValue) => ({
        value: enumValue,
        label: localizedIssueTypes[enumValue] ?? enumValue,
      })),
    [localizedIssueTypes],
  );

  // TODO: Replicate this in all form hooks that can potentially be used in a side panel
  // When the form is closed, reset the form to the default values
  useEffect(() => {
    if (open === false) {
      form.reset(defaultValues);
    }
  }, [defaultValues, form, open]);

  useEffect(() => {
    if (!issueType) return;

    form.setValue('issueType', issueType);
  }, [issueType, form]);

  const monitorCreateIssueFormIssueSubtypesLoad =
    useMonitorCreateIssueFormIssueSubtypesLoad();
  const monitorCreateIssueFormSubmit = useMonitorCreateIssueFormSubmit();

  useAllQueries({
    queries: [issueSubtypesQuery],
    onStart: () => {
      monitorCreateIssueFormIssueSubtypesLoad.start();
    },
    onSuccess: () => {
      monitorCreateIssueFormIssueSubtypesLoad.stop();
    },
    onError: () => {
      monitorCreateIssueFormIssueSubtypesLoad.error();
    },
  });

  useAllMutations({
    mutations: [createIssue],
    onStart: () => {
      monitorCreateIssueFormSubmit.start();
    },
    onSuccess: () => {
      monitorCreateIssueFormSubmit.stop();
    },
    onError: () => {
      monitorCreateIssueFormSubmit.error();
    },
  });

  const cleanErrors = (): void => {
    form.clearErrors();
    cleanServiceErrors();
  };

  const handleCreate = useEventCallback(() => {
    setTimeout(async () => {
      if (isAttachmentLoading) {
        enqueueSnackbar(t('issue:form.attachments.uploadInProgress.message'), {
          variant: 'info',
        });
        return;
      }

      const onValidSubmit = async (
        values: CreateIssueRequestDto,
      ): Promise<void> => {
        let { description } = values;
        if (information) {
          description = `${information} :: ${description}`;
        }

        // Filter out issue-type-specific fields based on the selected issue type
        const {
          classification,
          rootCause,
          recommendations,
          includeInAuditReport,
          remediation,
          ...baseValues
        } = values;

        const isAuditFinding = values.issueType === IssueTypeEnum.AUDIT_FINDING;
        const isControlDeficiency =
          values.issueType === IssueTypeEnum.CONTROL_DEFICIENCY;

        const issueTypeSpecificFields = {
          ...(isAuditFinding && {
            classification,
            rootCause,
            recommendations,
            includeInAuditReport,
          }),
          ...(isControlDeficiency && {
            classification,
            rootCause,
            remediation,
          }),
        };

        try {
          const issueResponse = await createIssue.mutateAsync({
            ...baseValues,
            ...issueTypeSpecificFields,
            issueSubtypeId:
              values.issueSubtypeId === ISSUE_SUBTYPE_NONE_VALUE
                ? undefined
                : values.issueSubtypeId,
            description,
            answerIds,
            source: sourceInfo,
          });

          onCreated?.(issueResponse);
        } catch (error) {
          onError?.(error);
          // eslint-disable-next-line no-console
          console.error(error);
        }
      };

      await form.handleSubmit(onValidSubmit)();
    }, 10);
  });

  const handleSave = useEventCallback(
    (saveIssueMutation: {
      mutateAsync: (
        values: CreateIssueRequestDto,
      ) => Promise<CreateIssueResponseDto>;
    }) => {
      setTimeout(async () => {
        if (isAttachmentLoading) {
          enqueueSnackbar(
            t('issue:form.attachments.uploadInProgress.message'),
            { variant: 'info' },
          );
          return;
        }

        const onValidSubmit = async (
          values: CreateIssueRequestDto,
        ): Promise<void> => {
          let { description } = values;
          if (information) {
            description = `${information} :: ${description}`;
          }

          // Filter out issue-type-specific fields based on the selected issue type
          // TODO: Refactor to use a more maintainable field filtering approach (RCP-32832)
          // https://diligentbrands.atlassian.net/browse/RCP-32832
          const {
            classification,
            rootCause,
            recommendations,
            includeInAuditReport,
            remediation,
            ...baseValues
          } = values;

          const isAuditFinding =
            values.issueType === IssueTypeEnum.AUDIT_FINDING;
          const isControlDeficiency =
            values.issueType === IssueTypeEnum.CONTROL_DEFICIENCY;

          const issueTypeSpecificFields = {
            ...(isAuditFinding && {
              classification,
              rootCause,
              recommendations,
              includeInAuditReport,
            }),
            ...(isControlDeficiency && {
              classification,
              rootCause,
              remediation,
            }),
          };

          try {
            await saveIssueMutation.mutateAsync({
              ...baseValues,
              ...issueTypeSpecificFields,
              description,
              answerIds,
              source: sourceInfo,
              ...(existingActions ? { actions: existingActions } : {}),
            });
          } catch (error) {
            onError?.(error);
            // eslint-disable-next-line no-console
            console.error(error);
          }
        };

        await form.handleSubmit(onValidSubmit)();
      }, 10);
    },
  );

  const rules = useMemo(
    () => ({
      title: {
        required: t('issue:form.errorMessages.title.required'),
      },
      issueType: {
        required: t('issue:form.errorMessages.issueType.required'),
      },
      issueSubtype: {
        required: t('issue:form.errorMessages.issueSubtype.required'),
      },
      description: {
        required: !isDraftIssuesEnabled
          ? t('issue:form.errorMessages.description.required')
          : undefined,
        validate: (
          _value: unknown,
          values: CreateIssueRequestDto,
        ): string | boolean => {
          const trimmedValue = (values.description || '').trim();

          if (trimmedValue.length > ISSUE_DESCRIPTION_LENGTH_MAX) {
            return t('issue:form.errorMessages.description.maxLength');
          }

          return true;
        },
      },
      impact: {
        validate: (
          _value: unknown,
          values: CreateIssueRequestDto,
        ): string | boolean => {
          const trimmedValue = (values.impact || '').trim();

          if (trimmedValue.length > ISSUE_IMPACT_LENGTH_MAX) {
            return t('issue:form.errorMessages.impact.maxLength');
          }
          return true;
        },
      },
      rootCause: {
        validate: (
          _value: unknown,
          values: CreateIssueRequestDto,
        ): string | boolean => {
          const trimmedValue = (values.rootCause || '').trim();
          if (trimmedValue.length > ROOT_CAUSE_LENGTH_MAX) {
            return t('issue:form.errorMessages.rootCause.maxLength');
          }
          return true;
        },
      },
      recommendations: {
        validate: (
          _value: unknown,
          values: CreateIssueRequestDto,
        ): string | boolean => {
          const trimmedValue = (values.recommendations || '').trim();
          if (trimmedValue.length > RECOMMENDATIONS_LENGTH_MAX) {
            return t('issue:form.errorMessages.recommendations.maxLength');
          }
          return true;
        },
      },
      dueDate: {
        validate: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          earlierThanSLA: (value: any, context: CreateIssueRequestDto) => {
            if (!value || SLAs.length === 0 || !context.severity) {
              return true;
            }
            return (
              isDateEarlierThanSLA(SLAs, context.severity, value as Date) ||
              t('issue:form.errorMessages.dueDate.earlierThanSLA')
            );
          },
        },
      },
    }),
    [isDraftIssuesEnabled, t, SLAs],
  );

  const labels = {
    issueType: t('issue:form.labels.issueType'),
    subtypeId: t('issue:form.labels.issueSubtype'),
    title: t('issue:form.labels.title'),
    relatedOrgUnit: t('issue:form.labels.relatedOrgUnit'),
    description: t('issue:form.labels.description'),
    impact: t('issue:form.labels.impact'),
    information: t('issue:form.labels.information'),
    source: t('issue:form.labels.source'),
    attachments: t('issue:form.labels.attachments'),
    dueDate: t('issue:form.labels.dueDate'),
    ownerId: t('issue:form.labels.owner'),
    severity: t('issue:form.labels.severity'),
    classification: t('issue:form.labels.classification'),
    rootCause: t('issue:form.labels.rootCause'),
    recommendations: t('issue:form.labels.recommendations'),
    includeInAuditReport: t('issue:form.labels.includeInAuditReport'),
  };

  const showControlDeficiencyExtraFields =
    isIssueTypesExtraFieldsEnabled && isControlDeficiencySelected;

  const showAuditFindingExtraFields =
    isIssueTypesExtraFieldsEnabled && isAuditFindingSelected;

  const fields: Record<LocalFields, ReactNode> = {
    title: (
      <ControlledTextField
        name='title'
        label={labels.title}
        control={form.control}
        rules={{
          required: t('issue:form.errorMessages.title.required'),
        }}
        maxLength={ISSUE_TITLE_LENGTH_MAX}
        inputProps={{
          readOnly,
        }}
      />
    ),
    issueType: (
      <ControlledTextField
        name='issueType'
        data-testid='categoryId'
        label={labels.issueType}
        control={form.control}
        rules={rules.issueType}
        options={issueTypeEnumOptions}
        onChange={() => {
          if (form.getValues('issueSubtypeId')) {
            form.setValue('issueSubtypeId', ISSUE_SUBTYPE_NONE_VALUE);
          }
        }}
        placeholder={t('common:inputs.listBox.empty')}
        inputProps={{
          readOnly: isEditingExistingIssue,
        }}
      />
    ),
    issueSubtype: (
      <ExtendedControlledAutocomplete
        name='issueSubtypeId'
        label={labels.subtypeId}
        data-testid='subcategoryId'
        control={form.control}
        rules={rules.issueSubtype}
        loading={issueSubtypesQuery.isFetching}
        options={[
          { id: ISSUE_SUBTYPE_NONE_VALUE, label: t('common:subtypes.none') },
          ...subtypesOptions.map((issueSubtype) => ({
            id: issueSubtype.id,
            label: issueSubtype.name,
          })),
        ]}
        onTextChange={(
          value: string,
          reason: AutocompleteInputChangeReason,
        ) => {
          handleInputChange(value, 'issueSubtype', reason);
        }}
        onChangeProp={(option) => {
          // When the clear button (X) is clicked, reset the entire form
          if (!option) {
            const currentIssueType = form.getValues('issueType');
            form.reset(defaultValues);
            if (currentIssueType) {
              form.setValue('issueType', currentIssueType);
            }
            // Reset template application tracking
            resetTemplateState();
          }
        }}
        placeholder={t('common:inputs.listBox.empty')}
        readOnly={isEditingExistingIssue}
      />
    ),
    govmapGroups: (
      <Controller
        name='govmapGroups'
        control={form.control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <GovmapGroupsSelector
            value={value ?? []}
            onChange={onChange}
            label={labels.relatedOrgUnit}
            error={!!error?.message}
            helperText={error?.message}
            readOnly={readOnly}
          />
        )}
      />
    ),
    description: (
      <ControlledTextField
        name='description'
        label={labels.description}
        control={form.control}
        rules={rules.description}
        minRows={4}
        multiline
        maxLength={ISSUE_DESCRIPTION_LENGTH_MAX}
        inputProps={{
          readOnly,
        }}
      />
    ),
    impact: (
      <ControlledTextField
        name='impact'
        label={labels.impact}
        control={form.control}
        minRows={4}
        multiline
        maxLength={ISSUE_IMPACT_LENGTH_MAX}
        inputProps={{
          readOnly,
        }}
      />
    ),
    attachments: !isEditingExistingIssue ? (
      <ControlledFileUploader
        disabled={createIssue.isPending || readOnly}
        control={form.control}
        label={labels.attachments}
        name='attachments'
        permissions={[]}
      />
    ) : null,
    information: information ? (
      <CollapsibleField header={labels.information}>
        <ReadOnlyText variant='body2'>{information}</ReadOnlyText>
      </CollapsibleField>
    ) : null,
    source: (
      <Stack spacing={0.5}>
        {isSourceExpected && (
          <ExtendedTypography variant='textSm'>
            {labels.source}
          </ExtendedTypography>
        )}
        {sourceInfo && (
          <Link href={sourceInfo.link} target='_blank'>
            <Typography fontWeight='bold'>
              {isSourceError
                ? ''
                : `${sourceInfo.name} - ${sourceInfo.entityType} - ${sourceInfo.id}`}
            </Typography>
          </Link>
        )}
      </Stack>
    ),
    dueDate: (
      <ControlledDateField
        name='dueDate'
        label={labels.dueDate}
        labelInfo={slaHelperText}
        control={form.control}
        rules={rules.dueDate}
        disablePast
        maxDateTime={slaMaxDateTime}
        data-testid='dueDate'
        readOnly={readOnly}
      />
    ),
    ownerId: (
      <Controller
        name='ownerId'
        control={form.control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <UserSelector
            value={value}
            initialValue={value || props.defaultValues?.ownerId}
            onChange={onChange}
            readOnly={readOnly}
            TextFieldProps={{
              label: labels.ownerId,
              'data-testid': 'ownerId',
              error: !!error?.message,
              helperText: error?.message,
            }}
          />
        )}
      />
    ),
    severity: (
      <ControlledTextField
        name='severity'
        label={labels.severity}
        control={form.control}
        options={severityValues}
        data-testid='severity'
        inputProps={{
          readOnly,
        }}
      />
    ),
    classification: (
      <ControlledTextField
        name='classification'
        label={labels.classification}
        data-testid='classification'
        control={form.control}
        options={classificationValues}
        inputProps={{
          readOnly,
        }}
      />
    ),
    rootCause: (
      <ControlledTextField
        name='rootCause'
        label={labels.rootCause}
        control={form.control}
        rules={rules.rootCause}
        data-testid='rootCause'
        minRows={4}
        multiline
        maxLength={ROOT_CAUSE_LENGTH_MAX}
        inputProps={{
          readOnly,
        }}
      />
    ),
    recommendations: (
      <ControlledTextField
        name='recommendations'
        label={labels.recommendations}
        control={form.control}
        rules={rules.recommendations}
        data-testid='recommendations'
        minRows={4}
        multiline
        maxLength={RECOMMENDATIONS_LENGTH_MAX}
        inputProps={{
          readOnly,
        }}
      />
    ),
    includeInAuditReport: (
      <Controller
        name='includeInAuditReport'
        control={form.control}
        render={({ field: { value, onChange } }) => (
          <FormControlLabel
            control={
              <Checkbox
                onChange={(e) => onChange(e.currentTarget.checked)}
                checked={Boolean(value)}
                data-testid='includeInAuditReport-checkbox'
                disabled={readOnly}
              />
            }
            label={labels.includeInAuditReport}
          />
        )}
      />
    ),
  };

  const formStateErrors = getFormStateErrors<CreateIssueRequestDto>(
    mapFormLabelsToFieldLabel(labels),
    rules,
    form.formState.errors,
  );
  const errors = joinFormErrorsWithServiceErrors(
    formStateErrors,
    serviceErrors,
  );

  const hasErrors = formHasErrorsToDisplay(errors);

  return {
    form,
    issueTypeSubtypeQueryIsLoading,
    isTemplateLoading: isTemplateFetching && !isEditingExistingIssue,
    createIssueIsLoading: createIssue.isPending,
    createIssueIsSuccess: createIssue.isSuccess,
    fields,
    errors,
    hasErrors,
    handleCreate,
    handleSave,
    cleanErrors,
    cleanForm: form.reset,
    shouldShowExtendedFields:
      issueTypeAndIssueSubtypeSelected || isEditingExistingIssue,
    showControlDeficiencyExtraFields,
    showAuditFindingExtraFields,
  };
};
